/**
 * 1.3.0 Administration → Media portal (/admin/portal, permission portal.manage): the portal's own section, kept apart
 * from PRISM's configuration. Sub-views by ?view= (the StorageAdmin pattern): Companies & users (the default),
 * Delivery specs, Upload rules, Branding and Settings. Stream P1's page: files under pages/admin/portal/ are P1's.
 */
import { useSearchParams } from 'react-router-dom';
import { Segmented } from '../../../components/Segmented';
import { usePortalSettings } from '../../../api/hooks/portalAdmin';
import { PortalCompanies } from './Companies';
import { PortalSpecs } from './Specs';
import { PortalRules } from './Rules';
import { PortalBranding, PortalSettings } from './Settings';

export type PortalAdminView = 'companies' | 'specs' | 'rules' | 'branding' | 'settings';
const VIEWS: Array<{ value: PortalAdminView; label: string }> = [
  { value: 'companies', label: 'Companies & users' },
  { value: 'specs', label: 'Delivery specs' },
  { value: 'rules', label: 'Upload rules' },
  { value: 'branding', label: 'Branding' },
  { value: 'settings', label: 'Settings' },
];

export function PortalAdmin() {
  const [params, setParams] = useSearchParams();
  const asked = params.get('view');
  const view: PortalAdminView = VIEWS.some((v) => v.value === asked) ? (asked as PortalAdminView) : 'companies';
  const settings = usePortalSettings().data;
  return (
    <div className="page portal-admin" data-testid="portal-admin">
      <div className="portal-admin-head">
        <Segmented role="tablist" label="Media portal" buttonRole="tab" buttonType="button" value={view}
          onChange={(v) => setParams(v === 'companies' ? {} : { view: v })}
          options={VIEWS.map((v) => ({ ...v, testId: `portal-view-${v.value}` }))} />
        {settings && (
          <span className={`pill ${settings.enabled && settings.effectiveOrigin ? 'accent' : ''}`} data-testid="portal-state"
            title={settings.effectiveOrigin ?? 'PRISM_PORTAL_ORIGIN is not set'}>
            {settings.enabled ? (settings.effectiveOrigin ? 'Portal on' : 'On, but no address') : 'Portal off'}
          </span>
        )}
      </div>
      <div style={{ height: 16 }} />
      <div data-testid={`portal-admin-${view}`}>
        {view === 'companies' && <PortalCompanies />}
        {view === 'specs' && <PortalSpecs />}
        {view === 'rules' && <PortalRules />}
        {view === 'branding' && <PortalBranding />}
        {view === 'settings' && <PortalSettings />}
      </div>
    </div>
  );
}
